import Link from "next/link";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import React, { ReactNode } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/shadcn/sidebar";

export const EntryIcon = ({ icon: Icon }: { icon: any }) => {
  if (!Icon) return null;

  // If it's already a JSX element, return it directly
  if (React.isValidElement(Icon)) {
    return Icon;
  }

  // Otherwise treat it as a component
  return (
    <Icon
      strokeWidth={1.25}
      size="16px"
      className="text-sidebar-foreground group-data-[active=true]/menu-button:text-sidebar-accent-foreground group-hover/menu-button:text-sidebar-foreground group-data-[variant=primary]/menu-button:text-white shrink-0 transition-colors"
    />
  );
};

export const EntryLabel = ({
  children,
  grow = true,
  className,
}: {
  children: ReactNode;
  // Off when something should sit right after the label (an expand chevron).
  grow?: boolean;
  className?: string;
}) => (
  <span
    className={cn(
      "pl-1 text-sm text-muted-foreground leading-tight truncate min-w-0",
      grow && "flex-1",
      "group-data-[active=true]/menu-button:text-sidebar-accent-foreground group-data-[active=true]/menu-button:font-medium group-hover/menu-button:text-sidebar-foreground group-data-[variant=primary]/menu-button:text-white transition-colors",
      className
    )}
  >
    {children}
  </span>
);

// A sidenav button that only opens and closes its inner pages; the inner
// pages are the ones that navigate. It is open while one of them is the
// current page, also when a link elsewhere lands there, and stands in for it
// on the collapsed icon rail. Collapsing the sidenav closes every inner menu
// (an open one would hide the parent's active mark); expanding it again opens
// only the one around the current page.
export const SideNavExpandableEntry = ({
  title,
  icon,
  hasActiveChild,
  testId,
  children,
}: {
  title: string;
  icon: ReactNode | LucideIcon;
  hasActiveChild: boolean;
  testId?: string;
  children: ReactNode;
}) => {
  const { state } = useSidebar();
  return (
    <Collapsible
      key={`${state}-${hasActiveChild}`}
      asChild
      defaultOpen={hasActiveChild && state === "expanded"}
      className="group/collapsible"
    >
      <SidebarMenuItem className="list-none">
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={hasActiveChild && state === "collapsed"}
            className="group/menu-button cursor-pointer data-[state=open]:bg-transparent data-[state=open]:hover:bg-sidebar-accent/50"
            data-testid={testId}
          >
            <EntryIcon icon={icon} />
            <EntryLabel grow={false}>{title}</EntryLabel>
            <ChevronDown className="ml-1 size-4 shrink-0 text-sidebar-foreground/50 transition-transform group-data-[state=open]/collapsible:rotate-180" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent
          className="CollapsibleContent"
          style={{ animationDuration: "120ms" }}
        >
          {/* The line stays under the parent's icon; 13px past it (less the
              sub-button's 1px pull and plus its 8px padding) lands the inner
              titles on the parent's, and the rows run to the right edge. */}
          <SidebarMenuSub className="mr-0 list-none pr-0 pl-[13px]">
            {children}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

export const SideNavSubEntry = ({
  title,
  isActive,
  testId,
  ...target
}: {
  title: string;
  isActive: boolean;
  testId?: string;
} & ({ href: string } | { onSelect: () => void })) => (
  <SidebarMenuSubItem className="list-none">
    {"href" in target ? (
      <SidebarMenuSubButton
        asChild
        isActive={isActive}
        className="cursor-pointer"
        data-testid={testId}
      >
        <Link href={target.href}>
          <span>{title}</span>
        </Link>
      </SidebarMenuSubButton>
    ) : (
      <SidebarMenuSubButton
        asChild
        isActive={isActive}
        className="cursor-pointer"
        data-testid={testId}
      >
        <button type="button" className="w-full" onClick={target.onSelect}>
          <span>{title}</span>
        </button>
      </SidebarMenuSubButton>
    )}
  </SidebarMenuSubItem>
);
